'use client';

import { type RefObject, useEffect, useRef, useState } from 'react';
import { useDebounce } from '@/hooks/use-debounce';

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

// Fraction of the whole scroll range, not per-tile distance — reaches the last index in a two-up
// layout and matches the snap target so the dot never desyncs from the snapped position.
const indexFromScroll = (scroller: HTMLElement, itemCount: number) => {
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const fraction = maxScroll > 0 ? scroller.scrollLeft / maxScroll : 0;
    return Math.round(fraction * (itemCount - 1));
};

const scrollToIndex = (scroller: HTMLElement, index: number, itemCount: number) => {
    if (itemCount < 2) {
        return;
    }
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const target = (index / (itemCount - 1)) * maxScroll;
    // Already aligned — bail so the snap can't re-fire on its own settle.
    if (Math.abs(target - scroller.scrollLeft) < 1) {
        return;
    }
    scroller.scrollTo({ left: target, behavior: 'smooth' });
};

type CarouselSnap = {
    activeIndex: number;
    canPaginate: boolean;
    goToIndex: (index: number) => void;
};

// `rootRef` must wrap the DraggableContainer; the scroller is its `.draggable-wrapper`. Snapping fires
// once scrolling has been quiet (debounced, same as useScrollDetection's own scroll handling), or on
export const useCarouselSnap = (rootRef: RefObject<HTMLElement | null>, itemCount: number): CarouselSnap => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [canPaginate, setCanPaginate] = useState(false);
    const scrollerRef = useRef<HTMLElement | null>(null);
    const isDraggingRef = useRef(false);
    const isTouchDragRef = useRef(false);

    const settle = () => {
        const scroller = scrollerRef.current;
        if (!scroller || isDraggingRef.current) {
            return;
        }
        const nearestIndex = indexFromScroll(scroller, itemCount);
        setActiveIndex(nearestIndex);
        scrollToIndex(scroller, nearestIndex, itemCount);
    };
    const [debouncedSettle, cancelDebouncedSettle] = useDebounce(settle, 150, { withCancel: true });

    useEffect(() => {
        const scroller = rootRef.current?.querySelector<HTMLElement>('.draggable-wrapper') ?? null;
        scrollerRef.current = scroller;
        if (!scroller || itemCount < 2) {
            return undefined;
        }

        const snapToNearest = () => scrollToIndex(scroller, indexFromScroll(scroller, itemCount), itemCount);

        const updateActive = () => {
            setActiveIndex(indexFromScroll(scroller, itemCount));
            // >1px overflow (sub-pixel rounding tolerance) means the row actually scrolls
            setCanPaginate(scroller.scrollWidth - scroller.clientWidth > 1);
        };
        const onScroll = () => {
            updateActive();
            debouncedSettle();
        };
        const onMouseDragStart = () => {
            isDraggingRef.current = true;
            isTouchDragRef.current = false;
            cancelDebouncedSettle();
        };
        const onTouchDragStart = () => {
            isDraggingRef.current = true;
            isTouchDragRef.current = true;
            cancelDebouncedSettle();
        };
        const onDragEnd = () => {
            if (!isDraggingRef.current) {
                return;
            }
            isDraggingRef.current = false;
            cancelDebouncedSettle();
            if (isTouchDragRef.current) {
                debouncedSettle();
            } else {
                snapToNearest();
            }
        };
        const onScrollEnd = () => debouncedSettle();

        updateActive();
        scroller.addEventListener('scroll', onScroll, { passive: true });
        scroller.addEventListener('scrollend', onScrollEnd);
        scroller.addEventListener('mousedown', onMouseDragStart);
        scroller.addEventListener('touchstart', onTouchDragStart, { passive: true });
        window.addEventListener('mouseup', onDragEnd);
        window.addEventListener('blur', onDragEnd);
        window.addEventListener('touchend', onDragEnd, { passive: true });
        window.addEventListener('touchcancel', onDragEnd, { passive: true });
        window.addEventListener('resize', updateActive);
        return () => {
            cancelDebouncedSettle();
            scroller.removeEventListener('scroll', onScroll);
            scroller.removeEventListener('scrollend', onScrollEnd);
            scroller.removeEventListener('mousedown', onMouseDragStart);
            scroller.removeEventListener('touchstart', onTouchDragStart);
            window.removeEventListener('mouseup', onDragEnd);
            window.removeEventListener('blur', onDragEnd);
            window.removeEventListener('touchend', onDragEnd);
            window.removeEventListener('touchcancel', onDragEnd);
            window.removeEventListener('resize', updateActive);
        };
    }, [rootRef, itemCount, debouncedSettle, cancelDebouncedSettle]);

    const goToIndex = (index: number) => {
        const scroller = scrollerRef.current;
        if (scroller) {
            scrollToIndex(scroller, clamp(index, 0, itemCount - 1), itemCount);
        }
    };

    return { activeIndex, canPaginate, goToIndex };
};
